import { Injectable } from '@angular/core';
import { Authentication } from '../core/authentication';
import { Cache } from '../core/data-xs';
import { CachedProfile } from '../models/CachedProfile';

const PREFER_LOGIN_FORM_KEY = 'lfPreferLoginForm';

@Injectable({
  providedIn: 'root'
})
export class CachedProfilesService {

  private readonly cache = new Cache<CachedProfile[]>('CachedProfiles');

  public getProfiles(): CachedProfile[] {
    const cached = this.cache.getItem();
    return Array.isArray(cached) ? cached.map(profile => ({ ...profile })) : [];
  }

  public hasProfiles(): boolean {
    return this.getProfiles().length > 0;
  }

  public hasProfile(username: string | null | undefined): boolean {
    if (!username) {
      return false;
    }

    return this.getProfiles().some(profile => profile.username === username);
  }

  public hasCurrentUserProfile(): boolean {
    return this.hasProfile(this.currentUsername());
  }

  public saveCurrentUser(): boolean {
    const user = Authentication.UserCache.getItem();
    const username = user?.Lid?.Gebruikersnaam || user?.Username;
    if (!username || !user?.PasswordHash) {
      return false;
    }

    this.upsert({
      username,
      password: user.PasswordHash,
      naam: user.Lid?.Lid || username,
      foto: user.Lid?.Foto
    });

    return true;
  }

  public upsert(profile: CachedProfile): void {
    const profiles = this.getProfiles();
    const stored: CachedProfile = {
      username: profile.username,
      password: profile.password,
      naam: profile.naam,
      foto: profile.foto
    };
    const index = profiles.findIndex(item => item.username === stored.username);

    if (index >= 0) {
      profiles[index] = stored;
    } else {
      profiles.push(stored);
    }

    this.cache.setItem(profiles);
  }

  public removeProfile(username: string): void {
    const profiles = this.getProfiles().filter(profile => profile.username !== username);

    if (profiles.length === 0) {
      this.cache.clearItem();
    } else {
      this.cache.setItem(profiles);
    }
  }

  public removeCurrentUserProfile(): void {
    const username = this.currentUsername();
    if (username) {
      this.removeProfile(username);
    }
  }

  public preferLoginFormOnNextVisit(): void {
    try {
      sessionStorage.setItem(PREFER_LOGIN_FORM_KEY, '1');
    } catch {
      // sessionStorage kan geblokkeerd zijn
    }
  }

  public consumePreferLoginForm(): boolean {
    try {
      const value = sessionStorage.getItem(PREFER_LOGIN_FORM_KEY);
      if (value) {
        sessionStorage.removeItem(PREFER_LOGIN_FORM_KEY);
        return true;
      }
    } catch {
      // sessionStorage kan geblokkeerd zijn
    }

    return false;
  }

  private currentUsername(): string | null {
    return Authentication.GetLid()?.Gebruikersnaam
      || Authentication.UserCache.getItem()?.Username
      || null;
  }

}
